import { createSelector } from '@ngrx/store';

import { EntityState, EntityAdapter, createEntityAdapter, Update } from '@ngrx/entity';

import { AppState } from 'store/root-reducer';
import * as pusher from 'store/actions/pusher.actions';

import {
  SearchPusher, CustomerPusher, NlpPusher, QuickscanNlpPusher,
  SearchPushEvent, QuickscanPusher, QuickscanSearchPusher,
} from 'modules/shared/models/pusher.models';
import {
  AddSearchPayload, RemoveSearchPayload, AddQuickscanSearchPayload, RemoveQuickscanSearchPayload,
  SetNlpPayload, RemoveNlpPayload, SetQuickscanNlpPayload, RemoveQuickscanNlpPayload
} from 'store/models/pusher.models';
import { Crp } from 'modules/shared/models/crp.models';

export interface SearchState extends EntityState<SearchPusher> { }
export interface CustomterState extends EntityState<CustomerPusher> { }

export interface QuickscanState extends EntityState<QuickscanPusher> { }
export interface QuickscanSearchState extends EntityState<QuickscanSearchPusher> { }

export interface NlpState extends EntityState<NlpPusher> { }
export interface QuickscanNlpState extends EntityState<QuickscanNlpPusher> { }

export interface State {
  searches: SearchState;
  customers: CustomterState;
  quickscans: QuickscanState;
  quickscanSearches: QuickscanSearchState;
  nlp: NlpState;
  quickscanNlp: QuickscanNlpState;
}

export const searchAdapter: EntityAdapter<SearchPusher> = createEntityAdapter<SearchPusher>({
  selectId: search =>
    `${search.generateReport}-${search.customerId}-${search.id}-${search.engineType}`
});

export const customerAdapter: EntityAdapter<CustomerPusher> = createEntityAdapter<CustomerPusher>();

export const quickscanSearchAdapter: EntityAdapter<QuickscanSearchPusher> = createEntityAdapter<QuickscanSearchPusher>({
  selectId: search =>
    `${search.id}-${search.engineType}`
});

export const quickscanAdapter: EntityAdapter<QuickscanPusher> = createEntityAdapter<QuickscanPusher>();

export const nlpAdapter: EntityAdapter<NlpPusher> = createEntityAdapter<NlpPusher>({
  selectId: nlp =>
    `${nlp.customerId}-${nlp.recordId}`
});

export const quickscanNlpAdapter: EntityAdapter<QuickscanNlpPusher> = createEntityAdapter<QuickscanNlpPusher>({
  selectId: quickscanNlp =>
    `${quickscanNlp.id}`
});

const initialState: State = {
  searches: searchAdapter.getInitialState(),
  customers: customerAdapter.getInitialState(),
  quickscans: quickscanAdapter.getInitialState(),
  quickscanSearches: quickscanSearchAdapter.getInitialState(),
  nlp: nlpAdapter.getInitialState(),
  quickscanNlp: quickscanNlpAdapter.getInitialState()
};

export function reducer(state: State = initialState, action: pusher.PusherActions): State {
  switch (action.type) {
    case pusher.PusherActionTypes.REMOVE_CUSTOMER: {
      const customerIds: number[] = (<number[]>action.payload);
      const updates: Update<CustomerPusher>[] = customerIds.map((customerId: number) => {
        return {
          id: customerId,
          changes: {
            isRemoved: true
          }
        };
      });
      return {
        ...state,
        customers: customerAdapter.updateMany(updates, state.customers)
      };
    }
    case pusher.PusherActionTypes.CLEAR_CUSTOMER: {
      return {
        ...state,
        searches: searchAdapter.removeMany(i => {
          const ids = (<number[]>action.payload);
          return ids.indexOf(i.customerId) >= 0;
        }, state.searches),
        customers: customerAdapter.removeMany(<number[]>action.payload, state.customers)
      };
    }
    case pusher.PusherActionTypes.SET_SCREENING: {
      const { customer, search } = (<AddSearchPayload>action.payload);
      return {
        ...state,
        searches: searchAdapter.upsertOne({ ...search, isRemoved: false }, state.searches),
        customers: customerAdapter.upsertOne({ ...customer, isRemoved: false }, state.customers)
      };
    }
    case pusher.PusherActionTypes.REMOVE_SCREENING: {
      const { customerId, id, engineType, generateReport, error } = (<RemoveSearchPayload>action.payload);
      const newState = searchAdapter.updateOne({
        id: `${generateReport}-${customerId}-${id}-${engineType}`,
        changes: {
          isRemoved: true,
          error: error
        }
      }, state.searches);
      const newCustomerState = state.customers;

      return {
        ...state,
        customers: newCustomerState,
        searches: newState
      };
    }
    case pusher.PusherActionTypes.REMOVE_QUICKSCAN: {
      return {
        ...state,
        quickscanSearches: quickscanSearchAdapter.removeMany(i => {
          const ids = (<string[]>action.payload);
          return ids.indexOf(i.id) >= 0;
        }, state.quickscanSearches),
        quickscans: quickscanAdapter.removeMany(<number[]>action.payload, state.quickscans)
      };
    }
    case pusher.PusherActionTypes.SET_QUICKSCAN_SEARCH: {
      const { quickscan, search } = (<AddQuickscanSearchPayload>action.payload);
      return {
        ...state,
        quickscanSearches: quickscanSearchAdapter.upsertOne({ ...search, isRemoved: false }, state.quickscanSearches),
        quickscans: quickscanAdapter.upsertOne(quickscan, state.quickscans)
      };
    }
    case pusher.PusherActionTypes.REMOVE_QUICKSCAN_SEARCH: {
      const { engineType, id } = (<RemoveQuickscanSearchPayload>action.payload);
      const newState = quickscanSearchAdapter.updateOne({
        id: `${id}-${engineType}`,
        changes: {
          isRemoved: true
        }
      }, state.quickscanSearches);
      const newQuickscanState = state.quickscans;
      return {
        ...state,
        quickscans: newQuickscanState,
        quickscanSearches: newState
      };
    }
    case pusher.PusherActionTypes.SET_NLP: {
      const { nlp } = (<SetNlpPayload>action.payload);
      return {
        ...state,
        nlp: nlpAdapter.upsertOne({ ...nlp, isRemoved: false }, state.nlp),
      };
    }
    case pusher.PusherActionTypes.REMOVE_NLP: {
      const { customerId, recordId, error } = (<RemoveNlpPayload>action.payload);
      const newState = nlpAdapter.updateOne({
        id: `${customerId}-${recordId}`,
        changes: {
          isRemoved: true,
          error: error
        }
      }, state.nlp);
      return {
        ...state,
        nlp: newState
      };
    }
    case pusher.PusherActionTypes.SET_QUICKSCAN_NLP: {
      const { quickscanNlp } = (<SetQuickscanNlpPayload>action.payload);
      return {
        ...state,
        quickscanNlp: quickscanNlpAdapter.upsertOne({ ...quickscanNlp, isRemoved: false }, state.quickscanNlp),
      };
    }
    case pusher.PusherActionTypes.REMOVE_QUICKSCAN_NLP: {
      const { id } = (<RemoveQuickscanNlpPayload>action.payload);
      const newState = quickscanNlpAdapter.updateOne({
        id: `${id}`,
        changes: {
          isRemoved: true
        }
      }, state.quickscanNlp);

      return {
        ...state,
        quickscanNlp: newState
      };
    }
    default: {
      return state;
    }
  }
}

const pusherSelector = (state: AppState) => state.pusher;

export const Selectors = {
  finishCustomerSummary: (customerId: number, crps: Crp[]) => createSelector(pusherSelector, (state: State) => {
    const groupByCrp = {};
    crps.forEach((crp: Crp) => {
      const searches = (<string[]>state.searches.ids).filter(id => id.includes(`-${customerId}-${crp.record}`));
      if (!searches || searches.length === 0) {
        groupByCrp[crp.record] = true;
      } else {
        let conclusion = true;
        searches.forEach((searchId: string) => {
          const entities = state.searches.entities[searchId];
          conclusion = conclusion && entities.isRemoved;
        });
        groupByCrp[crp.record] = conclusion;
      }
    });
    groupByCrp['all'] = true;
    const customer: CustomerPusher = state.customers.entities[customerId];
    if (!customer || !customer.screeningRequest.crpId) {
      Object.keys(groupByCrp).forEach((record: string) => {
        groupByCrp['all'] = groupByCrp['all'] && groupByCrp[record];
      });

      if (customer && customer.isRemoved === false) {
        groupByCrp['all'] = false;
      }
    }
    if (customer && customer.screeningRequest.crpId) {
      groupByCrp[customer.screeningRequest.recordId] = false;
    }

    return groupByCrp;
  }),
  finishCustomer: (eventFilter: SearchPushEvent) => createSelector(pusherSelector, (state: State) => {
    const finishedCustomerList: Array<CustomerPusher> = [];
    (<Array<number>>state.customers.ids).forEach((customerId: number) => {
      const searches = (<Array<string>>state.searches.ids).filter((id: string) => id.startsWith(`${eventFilter}-${customerId}-`));

      // if the customer is not in store or in store but all searches are marked as removed
      if (!searches || (searches.length > 0 && !searches.some((id: string) => !state.searches.entities[id].isRemoved))) {
        const customerPusher: CustomerPusher = Object.assign({}, state.customers.entities[customerId]);

        if (searches && searches.some((id: string) => state.searches.entities[id].error)) {
          customerPusher.error = true;
        }

        if (!customerPusher.isRemoved) {
          finishedCustomerList.push(customerPusher);
        }
      }
    });

    return finishedCustomerList;
  }),
  finishQuickscan: createSelector(pusherSelector, (state: State) => {
    const ids: QuickscanPusher[] = [];
    (<number[]>state.quickscans.ids).forEach((quickscanId: number) => {
      const searches = (<string[]>state.quickscanSearches.ids).filter(id => id.startsWith(`${quickscanId}-`));

      if (!searches
          || searches.length > 0
            && searches.filter(id => !state.quickscanSearches.entities[id].isRemoved).length === 0) {
        ids.push(state.quickscans.entities[quickscanId]);
      }
    });

    return ids;
  }),
  search: {
    selectAll: createSelector(pusherSelector, (state: State) => searchAdapter.getSelectors().selectAll(state.searches))
  },
  customer: customerAdapter.getSelectors(),
  nlp: createSelector(pusherSelector, (state: State) => nlpAdapter.getSelectors().selectAll(state.nlp)),
  quickScanSearches: createSelector(pusherSelector, (state: State) => {
    return quickscanSearchAdapter.getSelectors().selectAll(state.quickscanSearches);
  }),
  quickScanNlp: createSelector(pusherSelector, (state: State) => {
    return quickscanNlpAdapter.getSelectors().selectAll(state.quickscanNlp);
  }),
};
